<!--
  Card.vue
  Vue 3 component for rendering playing cards with CSS-based design

  Features:
  - Visual rendering for all card ranks (A, K, Q, J, 10-4) and suits (hearts, diamonds, clubs, spades, joker)
  - Wild card styling (Jokers, 2s)
  - Dead card styling (3s with red/black penalties)
  - Card back rendering for face-down cards
  - Selectable state with visual feedback
  - Hover effects and animations
  - Responsive sizing (small, medium, large)
  - Disabled state support
  - Preserves vanilla CSS classes for compatibility

  Props:
  - card: Card object with suit and rank (or null for card back)
  - selected: Whether card is selected (default: false)
  - disabled: Whether card is disabled (default: false)
  - selectable: Whether card can be selected (default: false)
  - faceDown: Whether to show card back (default: false)
  - size: Card size variant - 'normal', 'small', 'mini' (default: 'normal')
  - index: Optional card index for data attributes

  Emits:
  - click: When card is clicked, passes { card, index }

  Usage:
  <Card
    :card="{ suit: 'hearts', rank: 'A' }"
    :selected="false"
    :selectable="true"
    size="normal"
    @click="handleCardClick"
  />
-->

<script setup lang="ts">
import { computed } from 'vue'
import type { Card as CardType, Suit, Rank } from '@/composables/useCards'
import { useCards } from '@/composables/useCards'

// ============================================================================
// Props & Emits
// ============================================================================

interface Props {
  card?: CardType | null
  selected?: boolean
  disabled?: boolean
  selectable?: boolean
  faceDown?: boolean
  size?: 'normal' | 'small' | 'mini'
  index?: number | null
}

const props = withDefaults(defineProps<Props>(), {
  card: null,
  selected: false,
  disabled: false,
  selectable: false,
  faceDown: false,
  size: 'normal',
  index: null,
})

interface Emits {
  (e: 'click', payload: { card: CardType | null; index: number | null }): void
}

const emit = defineEmits<Emits>()

// ============================================================================
// Composables
// ============================================================================

const cards = useCards()

// ============================================================================
// Constants
// ============================================================================

const SUIT_SYMBOLS: Record<Suit, string> = {
  hearts: '♥',
  diamonds: '♦',
  spades: '♠',
  clubs: '♣',
  joker: '★',
}

const RANK_DISPLAY: Record<Rank, string> = {
  A: 'A',
  '2': '2',
  '3': '3',
  '4': '4',
  '5': '5',
  '6': '6',
  '7': '7',
  '8': '8',
  '9': '9',
  '10': '10',
  J: 'J',
  Q: 'Q',
  K: 'K',
  JOKER: 'JOKER',
}

// ============================================================================
// Computed Properties
// ============================================================================

/**
 * Determine if card should show as face down
 */
const showCardBack = computed(() => {
  return props.faceDown || !props.card
})

/**
 * Card suit (lowercase)
 */
const suit = computed(() => {
  return props.card?.suit?.toLowerCase() as Suit | undefined
})

/**
 * Card rank (uppercase)
 */
const rank = computed(() => {
  return String(props.card?.rank || '').toUpperCase() as Rank
})

/**
 * Check if card is a Joker
 */
const isJoker = computed(() => {
  return rank.value === 'JOKER' || suit.value === 'joker'
})

/**
 * Check if card is wild (Joker or 2)
 */
const isWild = computed(() => {
  return props.card ? cards.isWild(props.card) : false
})

/**
 * Check if card is a red 3
 */
const isRedThree = computed(() => {
  return props.card ? cards.isRedThree(props.card) : false
})

/**
 * Check if card is a black 3
 */
const isBlackThree = computed(() => {
  return props.card ? cards.isBlackThree(props.card) : false
})

/**
 * Get suit symbol for display
 */
const suitSymbol = computed(() => {
  return suit.value ? SUIT_SYMBOLS[suit.value] || '' : ''
})

/**
 * Get rank display text
 */
const rankDisplay = computed(() => {
  return RANK_DISPLAY[rank.value] || rank.value
})

/**
 * CSS classes for the card element
 */
const cardClasses = computed(() => {
  const classes = ['card']

  // Size variant
  if (props.size === 'small') classes.push('card-small')
  if (props.size === 'mini') classes.push('card-mini')

  // Card back
  if (showCardBack.value) {
    classes.push('card-back')
    return classes
  }

  // Suit class for coloring
  if (suit.value) classes.push(suit.value)

  // Card type classes
  if (isWild.value) classes.push('wild')
  if (isJoker.value) classes.push('joker')
  if (isRedThree.value) classes.push('red-three')
  if (isBlackThree.value) classes.push('black-three')

  // Interactive states
  if (props.selectable) classes.push('selectable')
  if (props.selected) classes.push('selected')
  if (props.disabled) classes.push('disabled')

  return classes
})

/**
 * Data attributes for the card element
 */
const cardDataAttrs = computed(() => {
  const attrs: Record<string, string> = {}
  if (props.index !== null && props.index !== undefined) {
    attrs['data-index'] = String(props.index)
  }
  return attrs
})

// ============================================================================
// Event Handlers
// ============================================================================

/**
 * Handle card click
 */
function handleClick() {
  if (props.disabled) return
  emit('click', {
    card: props.card || null,
    index: props.index || null,
  })
}
</script>

<template>
  <div
    :class="cardClasses"
    v-bind="cardDataAttrs"
    @click="handleClick"
  >
    <!-- Card Back Design -->
    <div v-if="showCardBack" class="card-back-design">
      <div class="card-back-border"></div>
      <div class="card-back-pattern"></div>
    </div>

    <!-- Card Face Design -->
    <template v-else>
      <!-- Joker Card -->
      <template v-if="isJoker">
        <div class="card-corner top-left">
          <span class="card-rank joker-rank">JOKER</span>
        </div>
        <div class="card-center joker-image-container">
          <img src="/images/cards/joker.png" alt="Joker" class="joker-image" />
        </div>
        <div class="card-corner bottom-right">
          <span class="card-rank joker-rank">JOKER</span>
        </div>
      </template>

      <!-- Regular Card -->
      <template v-else>
        <div class="card-corner top-left">
          <span class="card-rank">{{ rankDisplay }}</span>
          <span class="card-suit-small">{{ suitSymbol }}</span>
        </div>
        <div class="card-center">
          <span class="card-center-symbol">{{ suitSymbol }}</span>
        </div>
        <div class="card-corner bottom-right">
          <span class="card-rank">{{ rankDisplay }}</span>
          <span class="card-suit-small">{{ suitSymbol }}</span>
        </div>
      </template>
    </template>
  </div>
</template>

<style scoped>
/* All card styles are inherited from vanilla CSS (cards.css) */
/* This ensures visual consistency with the existing game */

.card.disabled {
  opacity: 0.5;
  cursor: not-allowed;
  pointer-events: none;
}

.card.disabled:hover {
  transform: none;
  box-shadow: inherit;
}
</style>
